import * as React from 'react';
import { useEffect, useReducer, useRef } from 'react';
import { Button } from 'reactstrap';
import { ProductFamilyDescription, ProductInventorySearchFilter } from '../../services/ProductInventoryService';
import { Code } from '../../models/Code';
import { MultiSelect } from '@progress/kendo-react-dropdowns';
import { ProductAttribute } from '../../models/ProductAttribute';
import ProductInventoryService from '../../services/ProductInventoryService';
import Loader from '../../components/shared/Loader';
import * as ArrayUtils from "typescript-array-utils";
import { toast } from 'react-toastify';
import GenericDropdown from '../../components/controls/GenericDropdown';

export type DetailsFilter = {
    ProductId: string;
    AttributeId: string;
    Codes: Code[];
}

type ProductSearchResultFiltersState = {
    isLoading: boolean;
    isLoadingFamilies: boolean;
    isNoResults: boolean;
    items: ProductAttribute[];
    showSecondaryFilters: boolean;
    totalItems: number;
    totalFamilies: number;
    selectedFilters: DetailsFilter[];
    productFamilyDescriptions: ProductFamilyDescription[];
    selectedFamilyDescription: ProductFamilyDescription;
}

const initialState: ProductSearchResultFiltersState = {
    isLoading: false,
    isLoadingFamilies: false,
    isNoResults: false,
    showSecondaryFilters: false,
    totalItems: 0,
    totalFamilies: 0,
    items: [],
    selectedFilters: [],
    productFamilyDescriptions: [],
    selectedFamilyDescription: undefined
}

function ProductSearchResultFilters(props: {
    searchFilter: ProductInventorySearchFilter,
    onAtrributeFiltersChanged?: (detailsFilters: DetailsFilter[]) => void;
}) {
    const mountRef = useRef(true);

    let [state, setState] = useReducer(
        (prevState: ProductSearchResultFiltersState, newState: Partial<ProductSearchResultFiltersState>) => ({ ...prevState, ...newState }),
        initialState
    );

    const loadProductFamilyDescriptions = () => {

        setState({
            isLoadingFamilies: true,
            productFamilyDescriptions: [],
            selectedFamilyDescription: undefined
        });

        ProductInventoryService.getProductFamilyDescriptions(props.searchFilter).then(x => {

            //console.log('getProductFamilyDescriptions', x)

            if (!mountRef.current)
              return;

            if (!x.hasErrors) {
                //console.log('setting getProductFamilyDescriptions', x)
                setState({
                    isLoadingFamilies: false,
                    productFamilyDescriptions: x.value.ProductFamilies,
                    //selectedFamilyDescription: 
                    totalFamilies: x.value.TotalFamilies
                });
            } else {
                toast.error("Error Loading Product Families");
            }

        })
    }

    const loadAllFilters = () => {

        setState({
            isLoading: true,
            items: [],
            selectedFilters: [],
            showSecondaryFilters: false
        });

        var payload = { ...props.searchFilter };
        payload.productAssembly = state.selectedFamilyDescription ? state.selectedFamilyDescription.AssemblyId : payload.productAssembly;

        ProductInventoryService.getFilters(payload).then(x => {

            if (!mountRef.current)
                return;

            //console.log('loadAllFilters', x);

            if (!x.hasErrors) {
                setState({
                    isLoading: false,
                    isNoResults: (x.value.Filters || []).length === 0,
                    items: x.value.Filters,
                    totalItems: (x.value.Filters || []).length,
                    //totalFamilies: x.value.TotalFamilies,
                    //productFamilyDescriptions: x.value.ProductFamilies
                });
            } else {
                toast.error("Error Loading Filters");
            }
        });

    }


    useEffect(() => {

        //loadAllFilters();
        loadProductFamilyDescriptions();


    }, [props.searchFilter]);

    useEffect(() => {

        loadAllFilters();
        //console.log('state.selectedFamilyDescription changed', state.selectedFamilyDescription);

    }, [state.selectedFamilyDescription])


    // on selected filters changed
    useEffect(() => {

        props.onAtrributeFiltersChanged(state.selectedFilters);

    }, [state.selectedFilters]);

    useEffect(() => {

        return () => {
            mountRef.current = false;
        }

    }, [])


    const primaryFilters = state.items.filter(x => x.PrimarySearch === true);
    const secondaryFilters = state.items.filter(x => x.AdvancedSearch === true);
    const itemsToRender = state.showSecondaryFilters ? primaryFilters.concat(secondaryFilters) : primaryFilters;

    return <React.Fragment>
        <h3>Filters</h3>
        <div className="form-group">
            <GenericDropdown
                dataSource={state.productFamilyDescriptions}
                getId={x => x.AssemblyId}
                renderItem={x => x.FullName}
                renderSelectedItem={x => x.FullName}
                onSelectedItemChanged={x => setState({ selectedFamilyDescription: x })}
                selectedValue={state.selectedFamilyDescription ? state.selectedFamilyDescription.AssemblyId : undefined}
                nullSelectionText="Select Product Family"
                dropdownMenuProps={{ className: "w-100-min" }}
                />
        </div>
        {state.totalFamilies > 1 && false && <span>Multiple Product Families found, please narrow the search.</span>}
        {state.isNoResults && state.totalFamilies === 0 && <span>NO FILTERS</span>}

        {(itemsToRender).map(x => {

            const selectedFilter = state.selectedFilters.find(f => x.AttributeId === f.AttributeId && x.ProductId === f.ProductId);
            const selectedCodes = selectedFilter ? selectedFilter.Codes : [];

            return <ProductFilter key={x.AttributeId}
                attribute={x}
                selected={selectedCodes}
                onSelectedChange={(codes: Code[]) => {

                    let newFiltersSet: DetailsFilter[] = [];

                    if (codes.length > 0) {

                        let filterIndex = state.selectedFilters.findIndex(f => f.AttributeId === x.AttributeId && f.ProductId === x.ProductId);

                        if (filterIndex > -1) {

                            let filter = state.selectedFilters[filterIndex];
                            filter.Codes = codes;
                            newFiltersSet = ArrayUtils.replace(state.selectedFilters, filterIndex, filter);

                        } else {

                            newFiltersSet = [...state.selectedFilters, { AttributeId: x.AttributeId, Codes: codes, ProductId: x.ProductId }];
                        }

                    } else {

                        newFiltersSet = state.selectedFilters.filter(f => f.AttributeId !== x.AttributeId);
                    }

                    //console.log('ProductFilter: onSelectedChange', codes);
                    //console.log('new filter set calculated', newFiltersSet);

                    setState({ selectedFilters: newFiltersSet });
                }}

            />
        })}

        {(state.isLoading || state.isLoadingFamilies) && <Loader />}
        {secondaryFilters.length > 0
            && !state.showSecondaryFilters
            && <Button color="link mx-0" onClick={() => { setState({ showSecondaryFilters: true }) }}>See more filters ({secondaryFilters.length}) </Button>}
    </React.Fragment>

}

export default ProductSearchResultFilters;


function ProductFilter(props: {
    attribute: ProductAttribute;
    selected: Code[];
    onSelectedChange: (codes: Code[]) => void;
}) {
    return <div className="form-group">
        <label>{props.attribute.Name}</label>
        <MultiSelect data={props.attribute.Codes}
            onChange={(e) => props.onSelectedChange(e.target.value)}
            value={props.selected}
            textField="Name"
            className="py-1"

        />
    </div>;
}